import React, { useEffect, useState } from 'react'
import styles from './LessonResults.module.css'

export interface LeaderboardEntry {
  id: string
  display_name: string
  avatar_url: string | null
  total_score: number
  rank: number
}

export interface RecapActivity {
  id: string
  sequence_number: number
  title: string
  activity_type: string
  score: number | null
  response_data_summary: {
    correct?: boolean
    total_correct?: number
    total_questions?: number
    tests_passed?: number
    total_tests?: number
    viewed?: boolean
  } | null
}

export interface LessonResultsData {
  lesson: { id: string; name: string }
  leaderboard: LeaderboardEntry[]
  your_rank: number | null
  your_score: number | null
  total_players: number
  activities: RecapActivity[]
  overall_score: number | null
}

interface LessonResultsProps {
  /** Fetches the end-of-lesson results for the current player's session. */
  fetchResults: () => Promise<{ success: boolean; data?: LessonResultsData; error?: string }>
  /** The current player's id, used to highlight their row in the leaderboard. */
  playerId?: string | null
  /** Called when the player taps the exit button. */
  onExit?: () => void
  exitLabel?: string
}

const RANK_MEDALS = ['🥇', '🥈', '🥉']

function encouragement(overall: number | null): string {
  if (overall === null) return 'Thanks for taking part — see you next time!'
  if (overall >= 90) return 'Outstanding work! You aced this lesson. 🏆'
  if (overall >= 70) return 'Great job! You really know your stuff. 🌟'
  if (overall >= 50) return 'Nice effort! Keep it up and you’ll master this. 💪'
  return 'Good try! Every attempt is a step forward — keep practising. 🚀'
}

/** Builds the small result badge text for one activity row. */
function activityBadge(activity: RecapActivity): { text: string; tone: 'good' | 'bad' | 'neutral' } {
  const summary = activity.response_data_summary
  if (summary?.viewed) return { text: 'Viewed', tone: 'neutral' }

  if (summary && summary.total_questions != null) {
    const correct = summary.total_correct ?? 0
    return { text: `${correct}/${summary.total_questions} correct`, tone: correct >= summary.total_questions ? 'good' : 'bad' }
  }
  if (summary && summary.total_tests != null) {
    const passed = summary.tests_passed ?? 0
    return { text: `${passed}/${summary.total_tests} tests`, tone: passed >= summary.total_tests ? 'good' : 'bad' }
  }
  if (summary && summary.correct != null) {
    return summary.correct ? { text: 'Correct', tone: 'good' } : { text: 'Incorrect', tone: 'bad' }
  }
  if (activity.score != null) {
    return { text: `${Math.round(activity.score)}%`, tone: activity.score >= 70 ? 'good' : 'bad' }
  }
  return { text: 'No answer', tone: 'neutral' }
}

export function LessonResults({ fetchResults, playerId, onExit, exitLabel = 'Join Another Lesson' }: LessonResultsProps) {
  const [data, setData] = useState<LessonResultsData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    fetchResults()
      .then((res) => {
        if (!active) return
        if (res.success && res.data) setData(res.data)
        else setError(res.error || 'Could not load your results')
      })
      .catch((e) => active && setError(e?.message || 'Could not load your results'))
      .finally(() => active && setLoading(false))
    return () => { active = false }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (loading) {
    return (
      <div className={styles.screen}>
        <div className={styles.card}>
          <div className={styles.spinner} />
          <p className={styles.tallyingText}>Tallying up the scores…</p>
        </div>
      </div>
    )
  }

  // If results fail to load, still acknowledge completion rather than dead-ending.
  if (error || !data) {
    return (
      <div className={styles.screen}>
        <div className={styles.card}>
          <h2 className={styles.title}>Lesson Complete!</h2>
          <p className={styles.subtitle}>{error || 'No results available.'}</p>
          {onExit && <button onClick={onExit} className={styles.exitBtn}>{exitLabel}</button>}
        </div>
      </div>
    )
  }

  const topThree = data.leaderboard.slice(0, 3)
  const youInTopThree = data.your_rank != null && data.your_rank <= 3

  return (
    <div className={styles.screen}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h2 className={styles.title}>Lesson Complete!</h2>
          <p className={styles.lessonName}>{data.lesson.name}</p>
          <p className={styles.encouragement}>{encouragement(data.overall_score)}</p>
        </div>

        {/* Personal score summary */}
        <div className={styles.youBox}>
          <div className={styles.youStat}>
            <span className={styles.youStatLabel}>Your Rank</span>
            <span className={styles.youStatValue}>
              {data.your_rank ? `#${data.your_rank}` : '—'}
              {data.total_players > 0 && <span className={styles.youStatSub}> of {data.total_players}</span>}
            </span>
          </div>
          <div className={styles.youStat}>
            <span className={styles.youStatLabel}>Your Score</span>
            <span className={styles.youStatValue}>{Math.round(data.your_score ?? 0)}</span>
          </div>
          {data.overall_score != null && (
            <div className={styles.youStat}>
              <span className={styles.youStatLabel}>Avg. Accuracy</span>
              <span className={styles.youStatValue}>{Math.round(data.overall_score)}%</span>
            </div>
          )}
        </div>

        {/* Top 3 leaderboard */}
        {topThree.length > 0 && (
          <div className={styles.section}>
            <p className={styles.sectionLabel}>Top Players</p>
            <div className={styles.leaderboard}>
              {topThree.map((entry, idx) => (
                <div
                  key={entry.id}
                  className={`${styles.lbRow} ${entry.id === playerId ? styles.lbRowYou : ''}`}
                >
                  <span className={styles.lbRank}>{RANK_MEDALS[idx] ?? `#${entry.rank}`}</span>
                  <span className={styles.lbName}>
                    {entry.display_name}
                    {entry.id === playerId && <span className={styles.youTag}> (You)</span>}
                  </span>
                  <span className={styles.lbScore}>{Math.round(entry.total_score)}</span>
                </div>
              ))}
              {/* Show the player's own row if they're outside the top 3 */}
              {!youInTopThree && data.your_rank != null && (
                <>
                  <div className={styles.lbEllipsis}>⋯</div>
                  <div className={`${styles.lbRow} ${styles.lbRowYou}`}>
                    <span className={styles.lbRank}>#{data.your_rank}</span>
                    <span className={styles.lbName}>
                      You<span className={styles.youTag}> (You)</span>
                    </span>
                    <span className={styles.lbScore}>{Math.round(data.your_score ?? 0)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Per-activity recap */}
        {data.activities.length > 0 && (
          <div className={styles.section}>
            <p className={styles.sectionLabel}>Activity Recap</p>
            <div className={styles.recapList}>
              {data.activities.map((activity) => {
                const badge = activityBadge(activity)
                return (
                  <div key={activity.id} className={styles.recapRow}>
                    <div className={styles.recapMain}>
                      <span className={styles.recapTitle}>
                        {activity.sequence_number}. {activity.title}
                      </span>
                      <span className={styles.recapType}>
                        {activity.activity_type.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <span className={`${styles.recapBadge} ${styles[`badge_${badge.tone}`]}`}>
                      {badge.text}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {onExit && (
          <button onClick={onExit} className={styles.exitBtn}>{exitLabel}</button>
        )}
      </div>
    </div>
  )
}
